/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Token Fetch - Token fetching with retry logic and expiry decoding
 */

import { metricsCollector } from "@/lib/monitoring/metrics";
import { logger } from "@/lib/utils/logger";
import { tokenCache } from "./token-cache";
import {
  FALLBACK_TOKEN_CACHE_TTL_MS,
  SUPABASE_JWT_TEMPLATE,
  TOKEN_MAX_RETRIES,
  TOKEN_RETRY_DELAY_MS,
  TOKEN_TIMEOUT_MS,
} from "./token-constants";
import type { ClerkGetToken } from "./token-types";

// ============================================================================
// TOKEN EXPIRY DECODING
// ============================================================================

/**
 * Decode JWT token to extract expiry timestamp
 * @param token - JWT token string
 * @returns Expiry timestamp in milliseconds, or null if unable to decode
 */
export function decodeTokenExpiry(token: string): number | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const decoded = JSON.parse(
      Buffer.from(
        payload.replace(/-/g, "+").replace(/_/g, "/"),
        "base64",
      ).toString("utf-8"),
    ) as { exp?: number };
    if (!decoded.exp) return null;
    // Subtract 60s as a safety buffer
    return decoded.exp * 1000 - 60_000;
  } catch {
    return null;
  }
}

// ============================================================================
// TOKEN FETCHING WITH RETRY
// ============================================================================

/**
 * Fetch token with retry logic
 * Implements exponential backoff for transient failures
 *
 * @param getToken - Clerk getToken function
 * @param cacheKey - Cache key for storing the token
 * @returns Token string or null if fetch failed
 */
export type FetchTokenWithRetryOptions = {
  /**
   * When false, the fetched token is not written to the module-level tokenCache.
   * Used for server-side callers where cross-request module caching is unsafe (CCP-713).
   */
  persistToTokenCache?: boolean;
};

export async function fetchTokenWithRetry(
  getToken: ClerkGetToken,
  cacheKey: string,
  options?: FetchTokenWithRetryOptions,
): Promise<string | null> {
  const persistToTokenCache = options?.persistToTokenCache !== false;
  const fetchStartTime = Date.now();
  let fetchRetryCount = 0;
  let fetchSuccess = false;
  const now = Date.now();

  try {
    // OPTIMIZATION: Retry logic with exponential backoff for transient failures
    let lastError: Error | unknown;
    for (let attempt = 0; attempt <= TOKEN_MAX_RETRIES; attempt++) {
      try {
        // Request Clerk token using the Supabase-specific template so JWT carries org/user IDs
        const attemptStart = Date.now();
        const tokenGetterPromise = getToken({
          template: SUPABASE_JWT_TEMPLATE,
        });

        let timeoutId: NodeJS.Timeout | null = null;
        const timeoutPromise = new Promise<string | null>((resolve) => {
          timeoutId = setTimeout(() => {
            logger.warn(
              `[getSupabaseToken] getToken() TIMEOUT after ${TOKEN_TIMEOUT_MS}ms`,
              {
                feature: "token-fetch",
                attempt: attempt + 1,
                totalAttempts: TOKEN_MAX_RETRIES + 1,
                timeoutMs: TOKEN_TIMEOUT_MS,
                isRetry: attempt > 0,
              },
            );
            resolve(null);
          }, TOKEN_TIMEOUT_MS);
        });

        const token = await Promise.race([tokenGetterPromise, timeoutPromise]);
        const attemptDuration = Date.now() - attemptStart;

        // Clean up timeout if token resolved first (prevent memory leak)
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Cache the token if we got one
        if (token) {
          const exp = decodeTokenExpiry(token);
          const expiresAt =
            typeof exp === "number" && Number.isFinite(exp)
              ? exp
              : now + FALLBACK_TOKEN_CACHE_TTL_MS;
          if (persistToTokenCache) {
            tokenCache.set(cacheKey, { token, expiresAt });
          }

          if (attempt > 0) {
            logger.info(
              `[getSupabaseToken] Token obtained on retry attempt ${attempt + 1}`,
              {
                feature: "token-fetch",
                attempt: attempt + 1,
                totalAttempts: TOKEN_MAX_RETRIES + 1,
                retryCount: attempt,
              },
            );
          }

          fetchSuccess = true;
          fetchRetryCount = attempt;
          return token;
        }

        // Token is null — distinguish instant null (Clerk says "no token") from actual timeout
        const wasInstantNull = attemptDuration < 100; // <100ms = Clerk returned null, not a timeout

        if (wasInstantNull) {
          // Clerk returned null immediately — no token available in this context.
          // Retrying won't help, it'll just return null again.
          logger.debug(
            "[getSupabaseToken] Clerk returned null instantly, skipping retry",
            {
              feature: "token-fetch",
              attemptDuration,
            },
          );
          break; // Exit retry loop — don't waste 500ms+ on a retry that will also return null
        }

        logger.debug(`[getSupabaseToken] Token is null, evaluating retry`, {
          feature: "token-fetch",
          attempt: attempt + 1,
          attemptDuration,
          willRetry: attempt < TOKEN_MAX_RETRIES,
        });

        if (attempt < TOKEN_MAX_RETRIES) {
          const retryDelay = TOKEN_RETRY_DELAY_MS * Math.pow(2, attempt);
          logger.debug(
            `[getSupabaseToken] Timed out, waiting ${retryDelay}ms before retry`,
            {
              feature: "token-fetch",
              retryDelayMs: retryDelay,
            },
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }
      } catch (error) {
        lastError = error;
        // Only retry on timeout-like errors, not other errors
        const isTimeoutError =
          error instanceof Error &&
          (error.message.includes("timeout") ||
            error.message.includes("TIMEOUT"));
        if (!isTimeoutError) {
          // Non-timeout error - don't retry
          logger.error(
            "[getSupabaseToken] Non-timeout error occurred, not retrying",
            {
              feature: "token-fetch",
              attempt: attempt + 1,
              error: error instanceof Error ? error.message : String(error),
              errorType: error instanceof Error ? error.name : typeof error,
            },
          );
          throw error;
        }
        // Timeout error - retry if attempts remaining
        if (attempt < TOKEN_MAX_RETRIES) {
          // OPTIMIZATION: Exponential backoff for retries (500ms → 1000ms)
          const retryDelay = TOKEN_RETRY_DELAY_MS * Math.pow(2, attempt);
          logger.warn(
            `[getSupabaseToken] Timeout error caught, waiting ${retryDelay}ms before retry`,
            {
              feature: "token-fetch",
              attempt: attempt + 2,
              totalAttempts: TOKEN_MAX_RETRIES + 1,
              retryDelayMs: retryDelay,
              backoffMultiplier: Math.pow(2, attempt),
              error: error instanceof Error ? error.message : String(error),
            },
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          logger.info(`[getSupabaseToken] Retrying after timeout error`, {
            feature: "token-fetch",
            attempt: attempt + 2,
            totalAttempts: TOKEN_MAX_RETRIES + 1,
          });
          continue; // Retry
        }
      }
    }

    // All attempts failed
    const latency = Date.now() - fetchStartTime;
    logger.warn(
      `[getSupabaseToken] No token obtained after ${TOKEN_MAX_RETRIES + 1} attempts - may cause auth issues`,
      {
        feature: "token-fetch",
        totalAttempts: TOKEN_MAX_RETRIES + 1,
        retryCount: fetchRetryCount,
        latency,
        error:
          lastError instanceof Error
            ? lastError.message
            : lastError
              ? String(lastError)
              : "timeout",
      },
    );

    // Record metrics for failed token fetch
    metricsCollector.record({
      operation: "token:fetch:failure",
      latency,
      errorRate: 1,
      payloadSize: 0,
      endpoint: "getSupabaseToken",
    });

    return null;
  } catch (error) {
    const latency = Date.now() - fetchStartTime;
    logger.error("[getSupabaseToken] Unexpected error during token fetch", {
      feature: "token-fetch",
      error: error instanceof Error ? error.message : String(error),
      errorType: error instanceof Error ? error.name : typeof error,
      latency,
      retryCount: fetchRetryCount,
    });

    // Record metrics for error
    metricsCollector.record({
      operation: "token:fetch:error",
      latency,
      errorRate: 1,
      payloadSize: 0,
      endpoint: "getSupabaseToken",
    });

    throw error;
  } finally {
    // Record success metrics if token was obtained
    if (fetchSuccess) {
      const latency = Date.now() - fetchStartTime;
      metricsCollector.record({
        operation:
          fetchRetryCount > 0
            ? "token:fetch:success:retry"
            : "token:fetch:success",
        latency,
        errorRate: 0,
        payloadSize: 0,
        endpoint: "getSupabaseToken",
      });
    }
  }
}
